import { z } from 'zod'

import {
  bytesSchema,
  idSchema,
  isoInstantSchema,
  limitSchema,
  moneyPaisa,
  planCodeSchema,
  ratioSchema,
  slugSchema,
  sortSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * P13–P14 · Usage and health (screens 08 and 05).
 *
 * "Near a limit" is **≥ 80% of any quota** (§4.17) — computed server-side so
 * the console and the upgrade prompt in the app cannot disagree about who is
 * about to hit a wall.
 */
export const usageRowSchema = z.object({
  tenantId: idSchema,
  slug: slugSchema,
  name: z.string(),
  planCode: planCodeSchema,
  animals: z.number().int().nonnegative(),
  animalLimit: limitSchema,
  users: z.number().int().nonnegative(),
  userLimit: limitSchema,
  storageBytes: bytesSchema,
  storageLimitBytes: limitSchema,
  /** The highest of the three ratios — what makes the row "near a limit". */
  worstUtilisation: ratioSchema,
  /** The plan that would fit, and what the move is worth per month. */
  suggest: z
    .object({ planCode: planCodeSchema, upliftPaisa: moneyPaisa() })
    .nullish(),
})

export const usageQuery = cursorQuerySchema.extend({
  nearLimit: z.coerce.boolean().optional(),
  planCode: planCodeSchema.optional(),
  sort: sortSchema(['-worstUtilisation', '-storage', '-animals', 'name'] as const, '-worstUtilisation'),
})

export const usageSummaryResponse = z.object({
  storageUsedBytes: bytesSchema,
  storageLimitBytes: limitSchema,
  growthBytesPerMonth: bytesSchema,
  hostingCostPaisa: moneyPaisa(),
  /** Hosting as a share of revenue. The number that decides the video policy. */
  hostingShare: ratioSchema.optional(),
  nearLimitCount: z.number().int().nonnegative(),
  breakdown: z.array(z.object({ label: z.string(), bytes: bytesSchema })),
  /**
   * "The lever" — how much storage originals are costing, and what the bill
   * looks like if they are dropped after transcoding (U1, undecided).
   */
  lever: z.object({
    originalsBytes: bytesSchema,
    savedBytes: bytesSchema,
    billAfterPaisa: moneyPaisa(),
  }),
  rows: z.array(usageRowSchema),
})

/* ── P14 · GET /platform/health ───────────────────────────────────────────── */

export const HEALTH_ACTIONS = ['NOTIFY_OWNER', 'SUCCEEDED', 'SUGGEST_TRADER', 'INVESTIGATE'] as const

export const healthItemSchema = z.object({
  id: idSchema,
  what: z.string(),
  detail: z.string().nullish(),
  tenantId: idSchema.nullish(),
  tenantName: z.string().nullish(),
  at: isoInstantSchema,
  action: z.enum(HEALTH_ACTIONS),
})

export const healthSummaryResponse = z.object({
  backupsDone: z.number().int().nonnegative(),
  backupsTotal: z.number().int().nonnegative(),
  retries: z.number().int().nonnegative(),
  videoQueue: z.number().int().nonnegative(),
  longestWaitMinutes: z.number().nonnegative(),
  failed24h: z.number().int().nonnegative(),
  storageUsedBytes: bytesSchema,
  storageLimitBytes: limitSchema,
  /** The "needs a look" list — every row is something a person must decide. */
  items: z.array(healthItemSchema),
  biggestStores: z.array(
    z.object({ tenantId: idSchema, name: z.string(), bytes: bytesSchema }),
  ),
})

export type UsageRow = z.infer<typeof usageRowSchema>
export type UsageQuery = z.input<typeof usageQuery>
export type UsageSummaryResponse = z.infer<typeof usageSummaryResponse>
export type HealthSummaryResponse = z.infer<typeof healthSummaryResponse>
